import axios from 'axios';

import {
  isBrowserOffline,
  isNetworkFailure,
  readOfflineFallback,
  rememberGetResponse
} from './offlineReadThrough';
import { currentUserScope } from './sessionCache';
import { beginNetworkActivity, endNetworkActivity } from './networkActivity';


const RATE_LIMIT_KEY = 'rateLimit:sensitiveOp:unlockAt';
const SHOP_BOOTSTRAP_CACHE_KEY = 'shop:bootstrap:lite:v1';
const GET_DEDUPE_TTL_MS = 1200;
const DEFAULT_API_TIMEOUT_MS = 30000;
const DEFAULT_CRITICAL_API_TIMEOUT_MS = 60000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes for large file uploads
const inflightGetRequests = new Map();
const recentGetResponses = new Map();
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const API_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_TIMEOUT_MS, DEFAULT_API_TIMEOUT_MS);
const CRITICAL_API_TIMEOUT_MS = Math.max(
  API_TIMEOUT_MS,
  parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, DEFAULT_CRITICAL_API_TIMEOUT_MS)
);
const UPLOAD_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_UPLOAD_TIMEOUT_MS, DEFAULT_UPLOAD_TIMEOUT_MS);

const stableSerialize = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${key}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return String(value);
};

const buildGetRequestKey = (config = {}) => {
  const url = String(config.url || '');
  const method = String(config.method || 'get').toLowerCase();
  const params = stableSerialize(config.params || {});
  // Do not share an in-flight or recent response between signed-in users, or
  // between callers with different timeouts / response formats.
  let token = '';
  try { token = localStorage.getItem('token') || ''; } catch (_) { /* no storage */ }
  const headers = config.headers || {};
  const adapterKey = config.adapter || '';
  return stableSerialize({ method, url, baseURL: config.baseURL || '', params,
    token, headers: typeof headers.toJSON === 'function' ? headers.toJSON() : headers,
    adapterKey, withCredentials: config.withCredentials,
    timeout: config.timeout, responseType: config.responseType || '',
    meta: config.meta || {}, scope: currentUserScope() });
};

const clearExpiredGetCacheEntries = () => {
  const now = Date.now();
  for (const [key, entry] of recentGetResponses.entries()) {
    if (!entry || (now - Number(entry.cachedAt || 0)) > GET_DEDUPE_TTL_MS) {
      recentGetResponses.delete(key);
    }
  }
};

const shouldUseCriticalTimeout = (url) => {
  const path = String(url || '').toLowerCase();
  if (!path) return false;
  return path.includes('/shop/bootstrap')
    || path.includes('/auth/login')
    || path.includes('/auth/verify');
};

const shouldUseUploadTimeout = (url) => {
  const path = String(url || '').toLowerCase();
  if (!path) return false;
  // Match bulk template routes ending with /upload or /import (or containing
  // /upload/…). /upload-template downloads are intentionally excluded.
  return /\/upload(?:\/|$)|\/import(?:\/|$)/i.test(path);
};

const resolveRequestTimeout = (config = {}) => {
  const configuredTimeout = Number(config.timeout);
  if (Number.isFinite(configuredTimeout)) {
    // An explicit timeout of 0 disables the client-side timeout entirely. Bulk
    // template uploads use this while their progress modal is visible so the
    // request can run to completion no matter how many rows are being imported.
    if (configuredTimeout === 0) return 0;
    if (configuredTimeout > 0) return configuredTimeout;
  }
  if (shouldUseUploadTimeout(config.url)) {
    return UPLOAD_TIMEOUT_MS;
  }
  if (shouldUseCriticalTimeout(config.url)) {
    return CRITICAL_API_TIMEOUT_MS;
  }
  return API_TIMEOUT_MS;
};

const emitGlobalToast = (message, severity = 'info') => {
  if (typeof window === 'undefined') return;
  const text = String(message || '').trim();
  if (!text) return;
  window.dispatchEvent(new CustomEvent('app:notify', {
    detail: { message: text, severity }
  }));
};

const isPermissionDenied = (error) => Number(error?.response?.status) === 403;

export const isAccessDeniedError = (error) => !!(error && (error.isAccessDenied || Number(error?.response?.status) === 403));

const TOAST_VERB_BY_METHOD = {
  post: { success: 'created', error: 'create' },
  put: { success: 'updated', error: 'update' },
  patch: { success: 'updated', error: 'update' },
  delete: { success: 'deleted', error: 'delete' }
};

const TOAST_ACTION_BY_SEGMENT = {
  approve: { success: 'approved', error: 'approve' },
  archive: { success: 'archived', error: 'archive' },
  cancel: { success: 'cancelled', error: 'cancel' },
  close: { success: 'closed', error: 'close' },
  complete: { success: 'completed', error: 'complete' },
  disable: { success: 'disabled', error: 'disable' },
  enable: { success: 'enabled', error: 'enable' },
  export: { success: 'exported', error: 'export' },
  import: { success: 'imported', error: 'import' },
  reconcile: { success: 'reconciled', error: 'reconcile' },
  reject: { success: 'rejected', error: 'reject' },
  reopen: { success: 'reopened', error: 'reopen' },
  restore: { success: 'restored', error: 'restore' },
  run: { success: 'ran', error: 'run' },
  send: { success: 'sent', error: 'send' },
  start: { success: 'started', error: 'start' },
  stop: { success: 'stopped', error: 'stop' },
  submit: { success: 'submitted', error: 'submit' },
  sync: { success: 'synced', error: 'sync' },
  test: { success: 'tested', error: 'test' },
  unarchive: { success: 'unarchived', error: 'unarchive' },
  unassign: { success: 'unassigned', error: 'unassign' },
  verify: { success: 'verified', error: 'verify' },
  void: { success: 'voided', error: 'void' }
};

const TOAST_RESOURCE_SEGMENT_IGNORED = new Set(['api', 'shop', 'auth', 'v1', 'v2', 'v3']);

const TOAST_RESOURCE_LABEL_OVERRIDES = {
  ap: 'AP',
  ar: 'AR',
  bom: 'BOM',
  cogs: 'COGS',
  gl: 'GL',
  grn: 'GRN',
  grns: 'GRNs',
  hr: 'HR',
  pos: 'POS'
};

const toNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const parseRequestBody = (config) => {
  const raw = config?.data;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const getPathSegments = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return [];

  let path = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname || '/';
    } catch (_) {
      path = raw;
    }
  }

  return path
    .split('?')[0]
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const isAuthLoginRequest = (config) => {
  const segments = getPathSegments(config?.url).map((segment) => segment.toLowerCase());
  return segments.includes('auth') && segments.includes('login');
};

const isLikelyIdentifierSegment = (segment) => {
  const value = String(segment || '').trim();
  if (!value) return true;
  if (/^\d+$/.test(value)) return true;
  if (/^tmp-\d+$/i.test(value)) return true;
  if (/^[0-9a-f]{8,}$/i.test(value)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) return true;
  return false;
};

const singularizeWord = (value) => {
  if (value.endsWith('ies') && value.length > 3) return `${value.slice(0, -3)}y`;
  if (value.endsWith('sses') && value.length > 4) return value.slice(0, -2);
  if (value.endsWith('ses') && value.length > 3) return value.slice(0, -2);
  if (value.endsWith('s') && !value.endsWith('ss') && value.length > 3) return value.slice(0, -1);
  return value;
};

const humanizeSegment = (segment, { singular = false } = {}) => {
  const words = String(segment || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return '';

  return words.map((word) => {
    const lower = String(word || '').toLowerCase();
    const normalized = singular ? singularizeWord(lower) : lower;
    if (TOAST_RESOURCE_LABEL_OVERRIDES[normalized]) {
      return TOAST_RESOURCE_LABEL_OVERRIDES[normalized];
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }).join(' ');
};

const toSentenceCase = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const extractServerMessage = (payload) => {
  if (!payload || typeof payload !== 'object') return '';

  const direct = toNonEmptyString(payload.message, payload.error, payload.detail, payload.title);
  if (direct) return direct;

  if (Array.isArray(payload.errors)) {
    for (const entry of payload.errors) {
      const message = (typeof entry === 'string')
        ? toNonEmptyString(entry)
        : toNonEmptyString(entry?.message, entry?.msg, entry?.detail);
      if (message) return message;
    }
  }

  if (payload.errors && typeof payload.errors === 'object') {
    for (const value of Object.values(payload.errors)) {
      if (Array.isArray(value)) {
        const message = toNonEmptyString(...value);
        if (message) return message;
        continue;
      }

      const message = (typeof value === 'string')
        ? toNonEmptyString(value)
        : toNonEmptyString(value?.message, value?.msg, value?.detail);
      if (message) return message;
    }
  }

  return '';
};

const resolveToastDescriptor = (config = {}) => {
  const method = String(config?.method || '').toLowerCase();
  const segments = getPathSegments(config?.url);
  const lowerSegments = segments.map((segment) => segment.toLowerCase());
  const payload = parseRequestBody(config);

  if (isAuthLoginRequest(config)) {
    return {
      successMessage: 'Signed in successfully',
      errorMessage: 'Sign in failed',
      subject: 'sign in',
      successVerb: 'completed',
      errorVerb: 'complete'
    };
  }

  if (lowerSegments.includes('shop') && lowerSegments.includes('settings')) {
    const section = toNonEmptyString(payload?.section);
    const subject = section ? `${humanizeSegment(section)} settings` : 'Settings';
    return {
      subject,
      successVerb: 'saved',
      errorVerb: 'save'
    };
  }

  const actionIndex = lowerSegments.reduce((latestIndex, segment, index) => (
    TOAST_ACTION_BY_SEGMENT[segment] ? index : latestIndex
  ), -1);

  const meaningfulSegments = lowerSegments
    .map((segment, index) => ({ segment, index }))
    .filter(({ segment }) => (
      !TOAST_RESOURCE_SEGMENT_IGNORED.has(segment)
      && !TOAST_ACTION_BY_SEGMENT[segment]
      && !isLikelyIdentifierSegment(segment)
    ));

  let resourceSegment = '';
  if (actionIndex >= 0) {
    const beforeAction = meaningfulSegments.filter(({ index }) => index < actionIndex);
    if (beforeAction.length > 0) {
      const nearest = beforeAction[beforeAction.length - 1];
      resourceSegment = segments[nearest.index] || '';
    }
  } else if (meaningfulSegments.length > 0) {
    const nearest = meaningfulSegments[meaningfulSegments.length - 1];
    resourceSegment = segments[nearest.index] || '';
  }

  const verbs = (actionIndex >= 0)
    ? TOAST_ACTION_BY_SEGMENT[lowerSegments[actionIndex]]
    : TOAST_VERB_BY_METHOD[method] || { success: 'completed', error: 'complete' };

  const shouldSingularize = method === 'post' && actionIndex < 0;
  const subject = humanizeSegment(resourceSegment, { singular: shouldSingularize }) || 'Record';

  return {
    subject,
    successVerb: verbs.success,
    errorVerb: verbs.error
  };
};

const buildContextualSuccessMessage = (config) => {
  const descriptor = resolveToastDescriptor(config);
  if (descriptor.successMessage) return descriptor.successMessage;
  return `${toSentenceCase(descriptor.subject || 'Record')} ${descriptor.successVerb || 'completed'} successfully`;
};

const buildContextualErrorMessage = (config) => {
  const descriptor = resolveToastDescriptor(config);
  if (descriptor.errorMessage) return descriptor.errorMessage;
  const subject = String(descriptor.subject || 'operation').toLowerCase();
  return `Failed to ${descriptor.errorVerb || 'complete'} ${subject}`;
};

const shouldToastForMethod = (config) => {
  const method = String(config?.method || '').toLowerCase();
  if (method !== 'post' && method !== 'put') return false;
  if (config?.meta?.suppressToast === true) return false;
  if (config?.headers?.['x-suppress-toast'] === '1') return false;
  return true;
};

const isReadOnlyMethod = (config) => {
  const method = String(config?.method || 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
};

const buildSuccessToastMessage = (response) => {
  const data = response?.data;
  const serverMessage = toNonEmptyString(data?.message, data?.data?.message);
  if (serverMessage) return serverMessage;
  return buildContextualSuccessMessage(response?.config);
};

const buildErrorToastMessage = (error) => {
  if (isPermissionDenied(error)) return 'Access denied';
  const data = error?.response?.data;
  const serverMessage = extractServerMessage(data);
  if (serverMessage) return serverMessage;
  if (error?.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (!error?.response) return 'Network error. Please check your connection and try again.';
  return buildContextualErrorMessage(error?.config);
};

// Axios' catch-all texts: replaced, never shown. Anything else (a rule thrown
// by the client itself) is a real sentence and is returned as-is.
const AXIOS_GENERIC_ERROR_MESSAGE = /^(network error|request failed with status code \d+)$/i;

/**
 * The same reasoning as `buildErrorToastMessage`, for screens that show the
 * failure *inline* (an Alert inside a dialog) instead of relying on the global
 * toast.
 *
 * Axios' own `message` for a rejected response is "Request failed with status
 * code 400" - an HTTP status, not an explanation. Callers that pasted that
 * straight into the UI (and then rethrew, which printed the whole AxiosError on
 * the console) left the clerk with no idea what to fix. This prefers whatever
 * the server said (`message`, `error`, `errors[]`) and falls back to the
 * caller's sentence for that particular action.
 */
export const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (isPermissionDenied(error)) return 'Access denied';

  const serverMessage = extractServerMessage(error?.response?.data);
  if (serverMessage) return serverMessage;

  if (error?.code === 'ECONNABORTED') return 'The request timed out. Please try again.';

  // Errors thrown in the client (a rule the backend never saw) carry their own
  // sentence and must survive; axios' generic texts are the ones to replace.
  const localMessage = toNonEmptyString(error?.message);
  if (localMessage && !AXIOS_GENERIC_ERROR_MESSAGE.test(localMessage)) return localMessage;

  if (error && !error.response) return 'Network error. Please check your connection and try again.';

  return toNonEmptyString(fallback) || 'Something went wrong. Please try again.';
};

const getInitializationRedirectUrl = (response) => {
  const redirectPath = response?.data?.redirect || '/initialization';
  const hasExplicitApiBase = !!process.env.REACT_APP_API_URL;
  const devFrontendPort = String(process.env.REACT_APP_DEV_FRONTEND_PORT || '').trim();
  const devBackendPort = String(process.env.REACT_APP_DEV_BACKEND_PORT || '').trim();

  if (!hasExplicitApiBase && typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    if (
      devFrontendPort
      && devBackendPort
      && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      && String(window.location.port || '') === devFrontendPort
    ) {
      return `http://localhost:${devBackendPort}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
    }
  }

  const fallbackApiBase = typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : '/api';
  const apiBase = process.env.REACT_APP_API_URL || fallbackApiBase;

  if (/^https?:\/\//i.test(redirectPath)) {
    return redirectPath;
  }

  try {
    const apiUrl = new URL(apiBase);
    const isLocalHost = apiUrl.hostname === 'localhost' || apiUrl.hostname === '127.0.0.1';
    const protocol = isLocalHost ? 'http:' : apiUrl.protocol;
    const backendOrigin = `${protocol}//${apiUrl.host}`;
    return `${backendOrigin}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
  } catch (_) {
    return redirectPath;
  }
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api'),
  timeout: API_TIMEOUT_MS,
  // Required so the httpOnly refresh cookie set by /auth/login is stored and
  // sent back to /auth/refresh. The backend CORS config already allows
  // credentials for the configured frontend origin.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => {
    if (status === 304) return true; // treat cached responses as success instead of throwing
    return status >= 200 && status < 300;
  }
});

const baseRequest = api.request.bind(api);

// ---------------------------------------------------------------------------
// Send-again (reads only)
// ---------------------------------------------------------------------------
// On a link that drops a third of its packets, a read that never reached the
// server is the common failure and costs nothing to repeat: no server-side
// effect, no user-visible action taken. Without this, a single lost SYN/RST made
// a whole screen come back empty and the operator pressed reload.
//
// Deliberately narrow:
//   * GET/HEAD only  - `performReadRequest` is the only caller.
//   * No 4xx/5xx     - `error.response` means the server answered; that is a real
//                      result (401, 422, 500) and must reach the caller.
//   * No timeouts    - the payload was already judged too slow to arrive once;
//                      a second full wait only lengthens the screen being stared
//                      at. The durable read cache is the better answer there.
//   * Bounded        - two extra attempts, 400 ms then 800 ms (+ jitter) so a
//                      downed uplink cannot turn into a retry storm.
const READ_RETRY_MAX = 2;
const READ_RETRY_BASE_DELAY_MS = 400;

const isRetryableReadFailure = (error) => {
  if (!error || error.response) return false;
  if (error.isAccessDenied) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return false;
  return isNetworkFailure(error);
};

const readWithNetworkRetry = async (config) => {
  // Retries change latency and failure semantics; enable them deliberately at
  // call sites instead of silently multiplying every GET on the page.
  const configured = Number(config?.meta?.readRetries ?? 0);
  const maxRetries = Number.isFinite(configured) ? Math.min(READ_RETRY_MAX, Math.max(0, configured)) : 0;

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await baseRequest(config);
    } catch (error) {
      if (attempt >= maxRetries || !isRetryableReadFailure(error)) throw error;
      const backoff = (READ_RETRY_BASE_DELAY_MS * (2 ** attempt)) + Math.floor(Math.random() * 150);
      await delay(backoff);
    }
  }
};

/**
 * One read, with the offline read-through applied.
 *
 * Deliberately independent of the dedupe map: `meta.disableGetDedupe` callers
 * (paginated fetches, polling) must still get last-known-good data during an
 * outage, so dedupe is layered on top rather than gated in front.
 *
 * @param {object} config axios config
 * @param {string|null} cacheKey dedupe key, or null for a non-deduped read
 */
const performReadRequest = async (config, cacheKey = null) => {
  const remember = (response) => {
    if (cacheKey) recentGetResponses.set(cacheKey, { cachedAt: Date.now(), response });
    return response;
  };

  try {
    // No link at all: answer from the durable read cache straight away. Waiting
    // on a request that cannot leave the machine is what made every screen look
    // hung during an outage. Without a cached copy we still try the network, so
    // a false "offline" report can never freeze the app.
    if (isBrowserOffline()) {
      const cached = await readOfflineFallback(config);
      if (cached) return remember(cached);
    }

    const response = await readWithNetworkRetry(config);
    // Best-effort: the caller never waits on IndexedDB.
    rememberGetResponse(config, response);
    return remember(response);
  } catch (error) {
    // The link died mid-flight (or timed out). Serve the last good copy of this
    // exact query rather than an empty screen - but never for a real server
    // answer such as 401/403/422/500.
    if (isNetworkFailure(error)) {
      const cached = await readOfflineFallback(config);
      if (cached) return remember(cached);
    }
    throw error;
  } finally {
    if (cacheKey) inflightGetRequests.delete(cacheKey);
  }
};

const trackedRequest = (configOrUrl, maybeConfig) => {
  // Every request the app makes passes through here (reads, writes, uploads), so
  // this is where services/networkActivity learns that the link is busy. The
  // background route prefetcher waits for this to go quiet before downloading
  // speculative chunks - that contention was what made a reload feel like 8s.
  beginNetworkActivity();
  try {
    return Promise.resolve(routeRequest(configOrUrl, maybeConfig)).finally(endNetworkActivity);
  } catch (error) {
    endNetworkActivity();
    throw error;
  }
};

const routeRequest = (configOrUrl, maybeConfig) => {
  const config = typeof configOrUrl === 'string'
    ? { ...(maybeConfig || {}), url: configOrUrl }
    : { ...(configOrUrl || {}) };
  config.timeout = resolveRequestTimeout(config);
  const method = String(config.method || 'get').toLowerCase();
  const isRead = method === 'get' || method === 'head';

  // Writes (and HEAD-less oddities) never touch the read cache.
  if (!isRead) {
    return baseRequest(config);
  }

  if (config?.meta?.disableGetDedupe === true || config.signal || config.cancelToken || /\/auth\//i.test(config.url || '')) {
    return performReadRequest(config);
  }

  clearExpiredGetCacheEntries();
  const cacheKey = buildGetRequestKey(config);
  const cached = recentGetResponses.get(cacheKey);
  if (cached && (Date.now() - cached.cachedAt) <= GET_DEDUPE_TTL_MS) {
    return Promise.resolve(cached.response);
  }

  if (inflightGetRequests.has(cacheKey)) {
    return inflightGetRequests.get(cacheKey);
  }

  const requestPromise = performReadRequest(config, cacheKey);

  inflightGetRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

api.request = trackedRequest;

// Axios binds its aliases to an internal context, so replacing `api.request`
// alone does not affect `api.get()` or `api.head()`. Route those methods through
// the wrapper explicitly; leave write aliases unchanged to preserve their body,
// form-data and interceptor semantics.
['get', 'head'].forEach((method) => {
  api[method] = (url, config = {}) => api.request({ ...config, method, url });
});

if (typeof window !== 'undefined') {
  // Fully-online mode: clear any stale offline queue entries from prior sessions.
  try {
    window.localStorage.removeItem('offline:outbox:v1');
  } catch (_) {
    // ignore storage access issues
  }

  // Suppress unhandled promise rejections for access-denied errors so they
  // do not bubble up to React error boundaries and cause full red screens.
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error && (error.isAccessDenied || isAccessDeniedError(error))) {
      event.preventDefault();
    }
  });
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') {
      try {
        const stored = localStorage.getItem(RATE_LIMIT_KEY);
        if (stored) {
          const unlockAt = parseInt(stored, 10);
          if (!Number.isNaN(unlockAt)) {
            const now = Date.now();
            if (unlockAt > now) {
              await delay(Math.max(unlockAt - now, 0));
            } else {
              localStorage.removeItem(RATE_LIMIT_KEY);
            }
          }
        }
      } catch (_) {
        // ignore storage access issues
      }
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Session expiry (single path)
// Every non-opted-out 401 funnels through here so that concurrent failures
// trigger exactly one cleanup + redirect. Previously each failed request
// removed the token and reassigned window.location, which produced duplicate
// toasts and repeated redirect attempts.
// ---------------------------------------------------------------------------
let sessionExpiryHandled = false;

const handleSessionExpiry = (error) => {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;

  try {
    localStorage.removeItem('token');
  } catch (_) {
    // ignore storage access issues
  }
  try {
    window.sessionStorage.removeItem(SHOP_BOOTSTRAP_CACHE_KEY);
  } catch (_) {
    // ignore storage access issues
  }

  const sessionReplaced = String(error?.response?.data?.code || '').toUpperCase() === 'SESSION_REPLACED';
  if (sessionReplaced) {
    emitGlobalToast('Your account was signed in on another device. Please login again.', 'warning');
  }

  if (typeof window === 'undefined') return;
  const path = String(window.location?.pathname || '').toLowerCase();
  if (path !== '/login') {
    window.location.href = '/login';
  }
};

// ---------------------------------------------------------------------------
// Silent access-token renewal
// The refresh token lives in an httpOnly cookie, so the browser can renew an
// expired access token without exposing the refresh token to JavaScript.
// ---------------------------------------------------------------------------
const AUTH_ENDPOINT_SEGMENTS = new Set([
  'login', 'refresh', 'logout', 'register', 'verify', 'forgot-password', 'reset-password'
]);

const isAuthEndpoint = (config) => {
  const segments = getPathSegments(config?.url).map((segment) => segment.toLowerCase());
  if (!segments.includes('auth')) return false;
  return segments.some((segment) => AUTH_ENDPOINT_SEGMENTS.has(segment));
};

let refreshRequestInFlight = null;

/**
 * Renew the access token via `POST /auth/refresh` (cookie-authenticated).
 * Uses a bare axios call so a 401 response cannot re-enter this interceptor.
 * Resolves to the new token, or null when the session is really over.
 */
const requestNewAccessToken = () => {
  if (refreshRequestInFlight) return refreshRequestInFlight;

  refreshRequestInFlight = axios
    .post(
      `${api.defaults.baseURL || ''}/auth/refresh`,
      {},
      { withCredentials: true, timeout: API_TIMEOUT_MS }
    )
    .then((response) => {
      const token = response?.data?.data?.access_token || response?.data?.access_token || '';
      if (!token) return null;
      try {
        localStorage.setItem('token', token);
      } catch (_) {
        // ignore storage access issues
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return token;
    })
    .catch(() => null)
    .finally(() => {
      refreshRequestInFlight = null;
    });

  return refreshRequestInFlight;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // A successful sign-in starts a fresh session, so allow a future expiry to
    // be handled again.
    if (isAuthLoginRequest(response?.config)) {
      sessionExpiryHandled = false;
    }
    if (shouldToastForMethod(response?.config)) {
      emitGlobalToast(buildSuccessToastMessage(response), 'success');
    }
    return response;
  },
  async (error) => {
    const accessDenied = isPermissionDenied(error);
    const skipAuthRedirect = error?.config?.meta?.skipAuthRedirect === true || isAuthLoginRequest(error?.config);

    if (isPermissionDenied(error)) {
      // Keep backend ACL strict, but avoid noisy toasts for read-only/background UI fetches.
      if (!isReadOnlyMethod(error?.config)) {
        emitGlobalToast('Access denied', 'error');
      }
      error.message = 'Access denied';
      if (error.response && error.response.data) {
        error.response.data.message = 'Access denied';
        error.response.data.ui_suppressed = true;
      }
      error.isAccessDenied = true;
      error.suppressUiError = true;
    }

    if (!accessDenied && shouldToastForMethod(error?.config)) {
      emitGlobalToast(buildErrorToastMessage(error), 'error');
    }

    const status = error.response?.status;
    if (status === 401) {
      if (skipAuthRedirect) {
        return Promise.reject(error);
      }

      // Before ending the session, try once to renew the access token using the
      // httpOnly refresh cookie set at sign-in.
      const originalRequest = error.config;
      const canAttemptRefresh = originalRequest
        && !originalRequest.__authRefreshAttempted
        && !isAuthEndpoint(originalRequest);

      if (canAttemptRefresh) {
        originalRequest.__authRefreshAttempted = true;
        const renewedToken = await requestNewAccessToken();
        if (renewedToken) {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${renewedToken}`
          };
        return baseRequest(originalRequest);
        }
      }

      // Token expired, invalid, or replaced by a newer sign-in / logout.
      handleSessionExpiry(error);
    }
    else if (status === 503 && error.response?.data?.code === 'NOT_INITIALIZED') {
      window.location.href = getInitializationRedirectUrl(error.response);
      return Promise.reject(error);
    }
    else if (status === 429) {
      const headers = error.response?.headers || {};
      const retryAfterHeader = headers['retry-after'] || headers['Retry-After'];
      let retryAfterSeconds = 0;
      if (retryAfterHeader) {
        const parsed = parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(parsed)) retryAfterSeconds = parsed;
      } else if (typeof error.response?.data?.retry_after === 'number') {
        retryAfterSeconds = error.response.data.retry_after;
      }
      if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
        retryAfterSeconds = 15; // sane fallback
      }
      const unlockAt = Date.now() + (retryAfterSeconds * 1000);
      try {
        localStorage.setItem(RATE_LIMIT_KEY, String(unlockAt));
        window.dispatchEvent(new CustomEvent('rate-limit', {
          detail: {
            key: 'sensitiveOp',
            unlockAt,
            retryAfterSeconds
          }
        }));
      } catch (_) {
        // ignore storage errors
      }

      const originalConfig = error.config;
      if (originalConfig && !originalConfig.__isRetry) {
        originalConfig.__isRetry = true;
        await delay(retryAfterSeconds * 1000);
        return baseRequest(originalConfig);
      }
    }
    return Promise.reject(error);
  }
);


// Helper to fetch routers (used in Network and Customers)
api.getRouters = function() {
  return api.get('/routers');
};

// ── Monitoring API helpers ─────────────────────────────────────

// Get monitoring overview (all routers, clients, bandwidth)
api.getMonitoringOverview = function() {
  return api.get('/monitoring/overview');
};

// Get single router monitoring detail
api.getRouterMonitoring = function(routerId) {
  return api.get(`/monitoring/router/${routerId}`);
};

// Get router history time-series
api.getRouterMonitoringHistory = function(routerId, params = {}) {
  return api.get(`/monitoring/router/${routerId}/history`, { params });
};

// Get router interfaces current state
api.getRouterInterfaces = function(routerId) {
  return api.get(`/monitoring/router/${routerId}/interfaces`);
};

// Get interface bandwidth history
api.getInterfaceHistory = function(routerId, ifaceName, params = {}) {
  return api.get(`/monitoring/router/${routerId}/interfaces/${encodeURIComponent(ifaceName)}/history`, { params });
};

// Get router WAN bandwidth history (summed across the router's WAN/upstream interfaces only)
api.getRouterBandwidthHistory = function(routerId, params = {}) {
  return api.get(`/monitoring/router/${routerId}/bandwidth-history`, { params });
};

// Get live clients (PPPoE, hotspot, DHCP leases)
api.getRouterClients = function(routerId) {
  return api.get(`/monitoring/router/${routerId}/clients`);
};

// Get monitoring events with filters
api.getMonitoringEvents = function(params = {}) {
  return api.get('/monitoring/events', { params });
};

// Acknowledge a monitoring event
api.acknowledgeMonitoringEvent = function(eventId) {
  return api.patch(`/monitoring/events/${eventId}/acknowledge`);
};

// Get alert configuration
api.getAlertConfig = function() {
  return api.get('/monitoring/alerts/config');
};

// Update alert configuration
api.updateAlertConfig = function(config) {
  return api.put('/monitoring/alerts/config', config);
};

// Send test notification
api.sendTestNotification = function(channel, recipient, routerId) {
  return api.post('/monitoring/alerts/test', { channel, recipient, routerId });
};

// Get poller status
api.getPollerStatus = function() {
  return api.get('/monitoring/poller/status');
};

// Trigger poll cycle now
api.runPollerNow = function() {
  return api.post('/monitoring/poller/run-now');
};

// Get clients with online status
api.getMonitoringClients = function(params = {}) {
  return api.get('/monitoring/clients', { params });
};

export default api;
